import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CompanyNewInterface } from "../../../../models/GeneralModule/CompanyNews";
import companyNewService from "../../../../services/general/companyNewService";
import { CACHE_KEY_COMPANYNEWS } from "../../../../utils/constants";

interface DeleteCompanyNewContext {
  previousCompanyNews: CompanyNewInterface[];
  deletedCompanyNew: CompanyNewInterface | undefined;
}

const useDeleteCompanyNew = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the companynew to delete
    DeleteCompanyNewContext
  >({
    mutationFn: companyNewService.Remove, // Remove should take a string ID

    onMutate: (companyNewId: string) => {
      const previousCompanyNews =
        queryClient.getQueryData<CompanyNewInterface[]>([
          CACHE_KEY_COMPANYNEWS,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<CompanyNewInterface[]>(
        [CACHE_KEY_COMPANYNEWS],
        previousCompanyNews.filter(
          (companyNew) => companyNew.id !== companyNewId
        ) // Use companyNewId to filter
      );

      return {
        previousCompanyNews,
        deletedCompanyNew: previousCompanyNews.find(
          (companyNew) => companyNew.id === companyNewId
        ),
      };
    },

    onError: (error, companyNewId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<CompanyNewInterface[]>(
        [CACHE_KEY_COMPANYNEWS],
        context.previousCompanyNews
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteCompanyNew;
